import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import "admin-lte/dist/css/adminlte.min.css";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import apiManager from "../../../api/apiManager";
import API_ENDPOINTS from "../../../api/apiEndpoints";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../../shared/Services/Loader/loaderService";
import FormLabel from "../../../shared/helpers/FormLabel";
import { useLocalization } from "../../../context/LocalizationContext";

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
  { value: "MXN", label: "MXN - Mexican Peso" },
  { value: "AUD", label: "AUD - Australian Dollar" },
];

// RFC-5321 compatible email regex (requires TLD)
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

// Shared React Select styles — keeps control height in sync with .form-control (30px)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    borderColor: state.isFocused ? "#80bdff" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
    "&:hover": { borderColor: "#80bdff" },
    fontSize: "14px",
    borderRadius: "5px",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "30px",
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "30px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  input: (base) => ({
    ...base,
    margin: "0",
    padding: "0",
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(0,0,0,0.3)",
    fontSize: "14px",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "14px",
  }),
};

const getBaseInitialValues = () => ({
  displayName: "",
  email: "",
  assignedDealer: "",
  address: "",
  phoneNumber: "",
  currency: "",
});

const CreateExternalUser = () => {
  const { t: localization } = useLocalization();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();

  const [dealerList, setDealerList] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [initialValues, setInitialValues] = useState(
    isEditMode ? null : getBaseInitialValues(),
  );

  const validationSchema = Yup.object({
    displayName: Yup.string()
      .required("Display Name is required")
      .min(2, "Display Name must be at least 2 characters")
      .max(100, "Display Name cannot exceed 100 characters")
      .matches(/^[^\d]/, "Display Name must not start with a number"),
    email: Yup.string()
      .required("Email is required")
      .matches(
        EMAIL_REGEX,
        "Please enter a valid email address (e.g. user@example.com)",
      ),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "exact-10-digits",
        "Phone number must be exactly 10 digits",
        (value) => {
          if (!value) return false;
          const digitsOnly = value.replace(/\D/g, "");
          return digitsOnly.length === 11;
        },
      ),
    address: Yup.string()
      .nullable()
      .max(250, "Address cannot exceed 250 characters"),
    currency: Yup.string().nullable(),
  });

  useEffect(() => {
    fetchDealerList();
    if (isEditMode) {
      fetchUserDetails(id);
    }
  }, [id, isEditMode]);

  // Once both dealerList and initialValues are ready, preselect the dealer
  useEffect(() => {
    if (isEditMode && initialValues?.assignedDealer && dealerList.length > 0) {
      const found = dealerList.find(
        (d) => String(d.value) === String(initialValues.assignedDealer),
      );
      setSelectedDealer(found || null);
    }
  }, [dealerList, initialValues?.assignedDealer, isEditMode]);

  const fetchDealerList = async () => {
    try {
      const response = await apiManager.get(API_ENDPOINTS.DEALER_GET_LIST);
      if (response.status === 200) {
        setDealerList(
          (response.data || []).map((d) => ({
            value: d.dealerId || d.id,
            label: d.dealerName || d.name || d.displayName,
          })),
        );
      }
    } catch (e) {
      showErrorMessage(e);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      showLoader();
      const response = await apiManager.get(
        `${API_ENDPOINTS.EXTERNAL_USER_GET_BY_ID}/${userId}`,
      );
      if (response.status === 200) {
        const data = response.data;
        setInitialValues({
          displayName: data.displayName || "",
          email: data.email || "",
          assignedDealer: data.assignedDealer || "",
          address: data.address || "",
          phoneNumber: data.phoneNumber || "",
          currency: data.currency || "",
        });
        if (data.currency) {
          setSelectedCurrency(
            CURRENCY_OPTIONS.find((c) => c.value === data.currency) || null,
          );
        }
      } else {
        showErrorMessage("Failed to fetch user details.");
      }
    } catch (e) {
      showErrorMessage(e);
    } finally {
      hideLoader();
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    showLoader();
    try {
      const payload = {
        displayName: values.displayName,
        email: values.email,
        assignedDealer: selectedDealer ? selectedDealer.value : null,
        address: values.address || null,
        phoneNumber: values.phoneNumber || null,
        currency: selectedCurrency ? selectedCurrency.value : null,
      };

      if (isEditMode) {
        payload.id = Number(id);
        payload.userId = id;
      }

      const endpoint = isEditMode
        ? API_ENDPOINTS.EXTERNAL_USER_UPDATE
        : API_ENDPOINTS.EXTERNAL_USER_CREATE;

      const response = isEditMode
        ? await apiManager.put(endpoint, payload)
        : await apiManager.post(endpoint, payload);

      if (response.status === 200) {
        showSuccessMessage(
          isEditMode
            ? "User updated successfully."
            : "User created successfully. An invitation email has been sent.",
        );
        navigate("/servicedeskusers");
      } else {
        showErrorMessage(
          isEditMode ? "Failed to update user." : "Failed to create user.",
        );
      }
    } catch (error) {
      showErrorMessage(error || "An error occurred.");
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  if (isEditMode && !initialValues) {
    return (
      <div className="container-fluid content-wrapper select-vehicle-content">
        <div className="content form-card">
          <div className="p-4 text-muted">Loading user details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="page-title">
          <div className="container-fluid p-0">
            <div className="row">
              <div className="col-md-6">
                <h1>{isEditMode ? "Edit User" : "Add User"}</h1>
              </div>
              <div className="col-md-6"></div>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <Formik
            initialValues={initialValues || getBaseInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, isSubmitting, setFieldValue }) => (
              <Form>
                <div className="row">
                  {/* Display Name */}
                  <div className="col-md-6 mb-3">
                    <FormLabel
                      htmlFor="displayName"
                      label="Display Name"
                      required
                    />
                    <Field
                      id="displayName"
                      name="displayName"
                      className={`form-control ${
                        touched.displayName && errors.displayName
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Enter Display Name"
                    />
                    {touched.displayName && errors.displayName && (
                      <div className="invalid-feedback">
                        {errors.displayName}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div className="col-md-6 mb-3">
                    <FormLabel htmlFor="email" label="Email" required />
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      className={`form-control ${
                        touched.email && errors.email ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Email Address"
                      disabled={isEditMode}
                    />
                    {isEditMode && (
                      <small className="text-muted">
                        Email cannot be changed after creation.
                      </small>
                    )}
                    {touched.email && errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  {/* Assign Shop */}
                  <div className="col-md-6 mb-3">
                    <FormLabel htmlFor="assignedDealer" label="Assign Shop" />
                    <Select
                      inputId="assignedDealer"
                      options={dealerList}
                      value={selectedDealer}
                      onChange={(option) => {
                        setSelectedDealer(option);
                        setFieldValue(
                          "assignedDealer",
                          option ? option.value : "",
                        );
                      }}
                      placeholder="Select a Shop"
                      classNamePrefix="select"
                      isClearable
                      styles={selectStyles}
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="col-md-6 mb-3">
                    <FormLabel htmlFor="phoneNumber" label="Phone Number" />
                    <Field
                      id="phoneNumber"
                      name="phoneNumber"
                      render={({ field, form }) => (
                        <PhoneInput
                          {...field}
                          placeholder="Enter Phone Number"
                          country="US"
                          countries={["US"]}
                          addInternationalOption={false}
                          international={false}
                          onChange={(value) =>
                            form.setFieldValue("phoneNumber", value)
                          }
                          className={`form-control ${
                            touched.phoneNumber && errors.phoneNumber
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      )}
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <div className="invalid-feedback d-block">
                        {errors.phoneNumber}
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  <div className="col-md-6 mb-3">
                    <FormLabel htmlFor="address" label="Address" />
                    <Field
                      as="textarea"
                      id="address"
                      name="address"
                      className={`form-control address-text-area ${
                        touched.address && errors.address ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Address"
                    />
                    {touched.address && errors.address && (
                      <div className="invalid-feedback">{errors.address}</div>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2 mt-2 mb-3">
                  <button
                    type="submit"
                    className="btn btn-success btn-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {isEditMode ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-cancel btn-sm"
                    onClick={() => navigate("/servicedeskusers")}
                  >
                    {localization("cancel")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default CreateExternalUser;